import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnvelopeSimple, SpinnerGap } from "@phosphor-icons/react";
import { api, errMsg } from "./api";
import { useAuth } from "./auth";
import { LogoMark } from "@/components/common";

export function OtpScreen() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(60);
  const sent = useRef(false);
  useEffect(() => { const t = setInterval(() => setWait((w) => Math.max(0, w - 1)), 1000); return () => clearInterval(t); }, []);

  const verify = async (c = code) => {
    if (c.length !== 6) return setErr("Enter the 6-digit code.");
    setBusy(true); setErr("");
    try {
      const r = await api.post("/auth/verify", { code: c });
      setUser(r.data);
      nav(["owner", "admin"].includes(r.data.role) ? "/admin" : "/dashboard", { replace: true });
    } catch (e) {
      setErr(errMsg(e)); setCode("");
      if (e?.response?.status === 429 || e?.response?.status === 401) setTimeout(logout, 1800);
    } finally { setBusy(false); }
  };
  const resend = async () => {
    setErr("");
    try { await api.post("/auth/resend"); sent.current = true; setWait(60); } catch (e) { setErr(errMsg(e)); }
  };
  const onChange = (v) => { const d = v.replace(/\D/g, "").slice(0, 6); setCode(d); if (d.length === 6) verify(d); };

  return (
    <div className="grid min-h-screen place-items-center bg-[#F5F7FA] px-5" data-testid="otp-screen">
      <div className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-sm">
        <LogoMark size={36} />
        <h1 className="mt-6 font-display text-3xl font-medium tracking-tight text-[#0E1729]">Check your email</h1>
        <p className="mt-2 flex items-start gap-2 text-sm text-slate-500"><EnvelopeSimple size={18} className="mt-0.5 shrink-0" /><span>We sent a 6-digit code to <b className="text-[#0E1729]" data-testid="otp-email">{user?.email}</b></span></p>
        <input autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => onChange(e.target.value)} disabled={busy} placeholder="••••••"
          data-testid="otp-code-input" className="mt-6 h-16 w-full rounded-2xl border-2 border-[#E3E8F0] text-center font-mono text-3xl tracking-[0.5em] text-[#0E1729] outline-none transition-colors duration-200 focus:border-[#0E1729]" />
        {err && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="otp-error">{err}</p>}
        {sent.current && !err && wait > 50 && <p className="mt-3 text-sm text-[#3E6B3C]" data-testid="otp-resent">A new code is on its way.</p>}
        <button onClick={() => verify()} disabled={busy} data-testid="otp-verify-button" className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#0E1729] font-semibold text-white disabled:opacity-60">
          {busy ? <SpinnerGap className="animate-spin" /> : "Continue"}
        </button>
        <div className="mt-5 flex items-center justify-between text-sm">
          <button onClick={resend} disabled={wait > 0} data-testid="otp-resend-button" className="font-semibold text-[#0E1729] disabled:text-slate-400">{wait > 0 ? `Resend in ${wait}s` : "Send a new code"}</button>
          <button onClick={logout} data-testid="otp-cancel-button" className="text-slate-500 hover:text-[#0E1729]">Use another account</button>
        </div>
      </div>
    </div>
  );
}
